import { useState, useEffect, useCallback } from 'react';
import { Card, Heading, Button, majorScale, CircleArrowLeftIcon, Pane, Pagination } from 'evergreen-ui';
import { PlusIcon } from 'evergreen-ui';
import AnnouncementList from './AnnouncementList';
import AddAnnouncementForm from './AddAnnouncementForm';
import EditAnnouncementForm from './EditAnnouncementForm';
import { fetchAnnouncements, getAnnouncementViews } from '../services/announcementServices';
import { useAuth } from '../services/authContext';
import { useLocation } from 'react-router-dom';
import { useRole } from '../contexts/RoleContext';

const BulletinBoard = ({ announcements, setAnnouncements }) => {
    const { isEducator, isOperator, effectiveIsDirector: isDirector } = useRole();
    const { user, isAuthenticated } = useAuth();
    const userId = user?.id;
    const [isAdding, setIsAdding] = useState(false);
    const [editedAnnouncement, setEditedAnnouncement] = useState(null);
    const [newAnnouncement, setNewAnnouncement] = useState({ title: '', body: '' });
    const [announcementViews, setAnnouncementViews] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const location = useLocation();

    const fetchCurrentPage = useCallback(async (p) => {
        try {
            setIsLoading(true);
            const response = await fetchAnnouncements(userId, isDirector, false, p, pageSize);
            if (response && response.status === 'success') {
                setAnnouncements(response.data);
                setTotalItems(response.total ?? response.data.length);
            } else {
                console.error('Errore: risposta non valida o vuota:', response);
                setAnnouncements([]);
                setTotalItems(0);
            }
        } catch (error) {
            console.error('Errore nel recupero degli annunci:', error);
            setAnnouncements([]);
        } finally {
            setIsLoading(false);
        }
    }, [userId, isDirector, pageSize, setAnnouncements]);

    useEffect(() => {
        fetchCurrentPage(page);
    }, [fetchCurrentPage, page, location.pathname, isAuthenticated]);

    useEffect(() => {
        const fetchAnnouncementViews = async () => {
            try {
                const normalizedAnnouncements = Array.isArray(announcements) ? announcements : (announcements?.data && Array.isArray(announcements.data) ? announcements.data : []);

                if (normalizedAnnouncements.length === 0) {
                    console.warn('Nessun annuncio disponibile per recuperare le visualizzazioni.');
                    return;
                }

                const announcementIds = normalizedAnnouncements.map((announcement) => announcement.id);
                const views = await getAnnouncementViews(announcementIds);
                setAnnouncementViews(views);

                if (!Array.isArray(views)) {
                    console.error('La risposta dell\'API non è un array valido:', views);
                    setAnnouncementViews({});
                    return;
                }

                const transformedViews = views.reduce((acc, item) => {
                    acc[item.announcementId] = item.views;
                    return acc;
                }, {});

                setAnnouncementViews(transformedViews);
            } catch (error) {
                console.error('Errore nel recupero delle visualizzazioni degli annunci:', error);
            }
        };

        if (Array.isArray(announcements) && announcements.length > 0) {
            fetchAnnouncementViews();
        }
    }, [announcements]);

    const handleEditAnnouncement = (index) => {
        setEditedAnnouncement({ ...announcements[index], index });
    };

    const handleCancelEdit = async () => {
        setEditedAnnouncement(null);
        try {
            await fetchCurrentPage(page);
        } catch (error) {
            console.error('Errore durante l\'aggiornamento delle comunicazioni:', error);
        }
    };

    return (
        <Card elevation={2} padding={24} background="tint1">
            <Heading size={700} marginBottom={16}>
                Bacheca
            </Heading>
                    {isAdding && isDirector ? (
                <>
                    <AddAnnouncementForm
                        newAnnouncement={newAnnouncement}
                        setNewAnnouncement={setNewAnnouncement}
                        setIsAdding={setIsAdding}
                        setAnnouncements={setAnnouncements}
                        userId={userId}
                                fetchAnnouncements={fetchCurrentPage}
                                onAnnouncementAdded={() => {
                                    fetchCurrentPage();
                                }}
                    />
                    <Button appearance="primary" intent="success" marginTop={majorScale(2)} onClick={() => setIsAdding(false)}>
                        Torna indietro <CircleArrowLeftIcon marginLeft={8} />
                    </Button>
                </>
            ) : editedAnnouncement && isDirector ? (
                    <EditAnnouncementForm
                    editedAnnouncement={editedAnnouncement}
                    setEditedAnnouncement={setEditedAnnouncement}
                    handleCancelEdit={handleCancelEdit}
                    fetchAnnouncements={fetchCurrentPage}
                    userId={userId}
                    setAnnouncements={setAnnouncements}
                />
            ) : (
                <>
                    {isLoading ? (
                        <>
                            <Heading size={400} color="muted">
                                Caricamento annunci in corso...
                            </Heading>
                        </>
                    ) : Array.isArray(announcements) && announcements.length > 0 ? (
                        <>
                            <AnnouncementList
                                announcements={announcements}
                                announcementViews={Object.values(announcementViews).flat()}
                                handleSelectAnnouncement={isDirector ? handleEditAnnouncement : undefined}
                                setAnnouncements={setAnnouncements}
                                userId={userId} 
                                userRole={isOperator ? 'operatore' : isDirector ? 'direttore' : 'educatore'}
                            />
                            <Pane display="flex" justifyContent="center" marginTop={16}>
                                <Pagination
                                    totalPages={Math.max(1, Math.ceil(totalItems / pageSize))}
                                    page={page}
                                    onPageChange={(p) => setPage(p)}
                                    onNextPage={() => setPage((prev) => Math.min(Math.max(1, Math.ceil(totalItems / pageSize)), prev + 1))}
                                    onPreviousPage={() => setPage((prev) => Math.max(1, prev - 1))}
                                />
                            </Pane>
                        </>
                    ) : (
                        <>
                            <Heading size={400} color="muted">
                                Nessun annuncio pubblicato.
                            </Heading>
                        </>
                    )}
                    {isDirector && (
                        <Button
                            appearance="primary"
                            intent="success"
                            marginTop={16}
                            onClick={() => setIsAdding(true)}
                        >
                            Aggiungi nuovo annuncio <PlusIcon marginLeft={8} />
                        </Button>
                    )}
                </>
            )}
        </Card>
    );
};

export default BulletinBoard;